import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePersistedState from '../../hooks/usePersistedState';
import * as api from './api';
import { LEVELS_BY_KEY, LEVELS } from './courseLevels';
import { buildCoursePayload, buildHierarchyPayload, buildTopicPayload } from './coursesPayload';
import { nextPosition, computeMove, sortByPosition } from './ordering';

const courseLevel = LEVELS_BY_KEY.course;
const topicLevel = LEVELS_BY_KEY.topic;

function parentLevelKeyOf(levelKey) {
    const found = LEVELS.find((l) => l.childKey === levelKey);
    return found ? found.key : null;
}

// Top-level state/data hook for the /courses/:id workspace page. Owns:
//  - the Course header itself
//  - a lazily-loaded, per-level-per-parent cache of the STRUCTURAL outline
//    tree ({[levelKey]: {[parentId]: node[]}}) for course->module->chapter
//    ->unit->lesson - a mutation at level L only ever invalidates
//    cache[L][parentId], nothing upstream/downstream, since every GET here
//    is a flat filtered list (no nested descendant payloads)
//  - a SEPARATE topicCache ({[hostLevelKey:hostId]: Topic[]}), since a
//    Topic can now attach directly under any of the 5 structural levels
//    instead of always being lesson's child - it needs its own cache
//    keyed by (hostLevelKey, hostId) pairs rather than a single parentId
//  - which nodes are expanded / selected (composite "levelKey:id" keys -
//    raw ids collide across tables, e.g. Module #3 and Chapter #3)
//  - create/update/delete/reorder mutations for both, incl. the pre-delete
//    child-existence guard required because none of these tables cascade-
//    delete on the backend (see api.js's hasChildren)
export default function useCourseWorkspace(courseId) {
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cache, setCache] = useState({});
    const [topicCache, setTopicCache] = useState({});
    // Persisted (not plain useState) so the tree looks "just as open" when
    // the user returns from a side trip (e.g. into an exercise's Builder
    // page and back) - a fresh mount of this hook restores the same
    // expanded/selected state instead of starting collapsed. Stored as a
    // plain array (usePersistedState round-trips through JSON.stringify,
    // which can't represent a Set) and converted to Set-like lookups below.
    const [expandedKeys, setExpandedKeys] = usePersistedState('courses:expanded:' + courseId, []);
    const [selected, setSelected] = usePersistedState('courses:selected:' + courseId, null);
    const [deletingKey, setDeletingKey] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.getOne(courseLevel.apiBase, courseId)
            .then((response) => setCourse(response.data))
            .catch((err) => {
                if (err.response && err.response.status === 401) navigate('/login');
                // GET /{id} on a missing row is a 500 here, not 404 - still
                // treated the same as "couldn't load" (course stays null).
            })
            .finally(() => setLoading(false));
    }, [courseId, navigate]);

    const getChildren = (levelKey, parentId) => (cache[levelKey] ? cache[levelKey][parentId] : undefined);

    const ensureChildren = (levelKey, parentId) => {
        const cached = getChildren(levelKey, parentId);
        if (cached !== undefined) return Promise.resolve(cached);
        const level = LEVELS_BY_KEY[levelKey];
        return api.getList(level.apiBase, level.filterParam, parentId).then((response) => {
            const list = Array.isArray(response.data) ? response.data : [];
            setCache((prev) => ({ ...prev, [levelKey]: { ...(prev[levelKey] || {}), [parentId]: list } }));
            return list;
        });
    };

    const getNode = (levelKey, parentId, id) => {
        const list = getChildren(levelKey, parentId);
        return list ? list.find((item) => item.id === id) : null;
    };

    // --- Topics: separate cache, keyed by which level hosts them ---
    const getTopics = (hostLevelKey, hostId) => topicCache[hostLevelKey + ':' + hostId];

    const ensureTopics = (hostLevelKey, hostId) => {
        const cacheKey = hostLevelKey + ':' + hostId;
        const cached = topicCache[cacheKey];
        if (cached !== undefined) return Promise.resolve(cached);
        return api.getList(topicLevel.apiBase, hostLevelKey, hostId).then((response) => {
            const list = Array.isArray(response.data) ? response.data : [];
            setTopicCache((prev) => ({ ...prev, [cacheKey]: list }));
            return list;
        });
    };

    const getTopicNode = (hostLevelKey, hostId, topicId) => {
        const list = getTopics(hostLevelKey, hostId);
        return list ? list.find((item) => item.id === topicId) : null;
    };

    const isExpanded = (levelKey, id) => expandedKeys.includes(levelKey + ':' + id);
    const toggleExpand = (levelKey, id) => {
        const key = levelKey + ':' + id;
        setExpandedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    };

    // Walks the whole structural chain (Course->Module->Chapter->Unit->
    // Lesson) fetching each level's children as needed - same lazy
    // `ensureChildren` the tree already uses one node at a time, just
    // driven recursively from the root instead of from a single click - so
    // every level's data ends up cached exactly like manually expanding
    // node-by-node would leave it. Topics themselves never appear in
    // expandedKeys (they have no childKey, nothing to expand), so the walk
    // only needs to follow the structural chain, not `canHostTopics`.
    const expandAll = () => {
        const collect = (levelKey, parentId) => {
            const level = LEVELS_BY_KEY[levelKey];
            if (!level.childKey) return Promise.resolve([]);
            const childLevel = LEVELS_BY_KEY[level.childKey];
            return ensureChildren(childLevel.key, parentId).then((children) => (
                Promise.all(children.map((node) => collect(childLevel.key, node.id))).then((nested) => {
                    const keys = children.map((node) => childLevel.key + ':' + node.id);
                    return keys.concat(...nested);
                })
            ));
        };
        collect('course', courseId).then((keys) => {
            setExpandedKeys((prev) => Array.from(new Set([...prev, ...keys])));
        });
    };
    const collapseAll = () => setExpandedKeys([]);

    const isSelected = (levelKey, id) => !!selected && selected.levelKey === levelKey && selected.id === id;
    // hostLevelKey is only meaningful for levelKey==='topic' (which of its 5
    // possible parent fields applies) - undefined/ignored otherwise.
    const select = (levelKey, id, parentId, hostLevelKey) => setSelected({ levelKey, id, parentId, hostLevelKey });

    const saveCourse = (changes) => {
        const payload = buildCoursePayload(course, changes);
        return api.updateOne(courseLevel.apiBase, courseId, payload).then((response) => {
            setCourse(response.data);
            return response.data;
        });
    };

    const createNode = (levelKey, parentId, values) => {
        const level = LEVELS_BY_KEY[levelKey];
        return ensureChildren(levelKey, parentId).then((list) => {
            const position = nextPosition(sortByPosition(list));
            const payload = buildHierarchyPayload(level, parentId, {}, {
                name: values.name, description: values.description || '', position,
            });
            return api.createOne(level.apiBase, payload);
        }).then((response) => {
            const created = response.data;
            setCache((prev) => {
                const current = (prev[levelKey] && prev[levelKey][parentId]) || [];
                return { ...prev, [levelKey]: { ...(prev[levelKey] || {}), [parentId]: [...current, created] } };
            });
            const parentLevelKey = parentLevelKeyOf(levelKey);
            if (parentLevelKey && parentLevelKey !== 'course') {
                const key = parentLevelKey + ':' + parentId;
                setExpandedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
            }
            return created;
        });
    };

    const updateNode = (levelKey, parentId, node, changes) => {
        const level = LEVELS_BY_KEY[levelKey];
        const payload = buildHierarchyPayload(level, parentId, node, changes);
        return api.updateOne(level.apiBase, node.id, payload).then((response) => {
            const updated = response.data;
            setCache((prev) => {
                const list = (prev[levelKey] && prev[levelKey][parentId]) || [];
                const nextList = list.map((item) => (item.id === updated.id ? updated : item));
                return { ...prev, [levelKey]: { ...(prev[levelKey] || {}), [parentId]: nextList } };
            });
            return updated;
        });
    };

    const reorderSibling = (levelKey, parentId, id, direction) => {
        const level = LEVELS_BY_KEY[levelKey];
        const list = sortByPosition(getChildren(levelKey, parentId) || []);
        const index = list.findIndex((item) => item.id === id);
        if (index === -1) return;
        const swap = computeMove(list, index, direction);
        if (!swap) return;
        Promise.all(swap.map((item) => api.updateOne(level.apiBase, item.id, buildHierarchyPayload(level, parentId, item))))
            .then((responses) => {
                setCache((prev) => {
                    const current = (prev[levelKey] && prev[levelKey][parentId]) || [];
                    const nextList = current.map((item) => {
                        const match = responses.find((r) => r.data.id === item.id);
                        return match ? match.data : item;
                    });
                    return { ...prev, [levelKey]: { ...(prev[levelKey] || {}), [parentId]: nextList } };
                });
            })
            .catch((err) => {
                console.error(err);
                window.alert('Не вдалося змінити порядок.');
            });
    };

    // --- Topics: create/update/delete/reorder, mirroring the generic
    // functions above but against topicCache + buildTopicPayload. ---
    const createTopic = (hostLevelKey, hostId, values) => {
        return ensureTopics(hostLevelKey, hostId).then((list) => {
            const position = nextPosition(sortByPosition(list));
            const payload = buildTopicPayload(hostLevelKey, hostId, {}, {
                name: values.name, description: values.description || '', position,
            });
            return api.createOne(topicLevel.apiBase, payload);
        }).then((response) => {
            const created = response.data;
            const cacheKey = hostLevelKey + ':' + hostId;
            setTopicCache((prev) => {
                const current = prev[cacheKey] || [];
                return { ...prev, [cacheKey]: [...current, created] };
            });
            // course's own topics render in an always-visible section (no
            // chevron to expand), same reasoning createNode already applies
            // to modules sitting directly under the course root.
            if (hostLevelKey !== 'course') {
                const key = hostLevelKey + ':' + hostId;
                setExpandedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
            }
            return created;
        });
    };

    const updateTopic = (hostLevelKey, hostId, node, changes) => {
        const payload = buildTopicPayload(hostLevelKey, hostId, node, changes);
        return api.updateOne(topicLevel.apiBase, node.id, payload).then((response) => {
            const updated = response.data;
            const cacheKey = hostLevelKey + ':' + hostId;
            setTopicCache((prev) => {
                const list = prev[cacheKey] || [];
                const nextList = list.map((item) => (item.id === updated.id ? updated : item));
                return { ...prev, [cacheKey]: nextList };
            });
            return updated;
        });
    };

    const reorderTopicSibling = (hostLevelKey, hostId, topicId, direction) => {
        const cacheKey = hostLevelKey + ':' + hostId;
        const list = sortByPosition(getTopics(hostLevelKey, hostId) || []);
        const index = list.findIndex((item) => item.id === topicId);
        if (index === -1) return;
        const swap = computeMove(list, index, direction);
        if (!swap) return;
        Promise.all(swap.map((item) => api.updateOne(topicLevel.apiBase, item.id, buildTopicPayload(hostLevelKey, hostId, item))))
            .then((responses) => {
                setTopicCache((prev) => {
                    const current = prev[cacheKey] || [];
                    const nextList = current.map((item) => {
                        const match = responses.find((r) => r.data.id === item.id);
                        return match ? match.data : item;
                    });
                    return { ...prev, [cacheKey]: nextList };
                });
            })
            .catch((err) => {
                console.error(err);
                window.alert('Не вдалося змінити порядок.');
            });
    };

    const deleteTopic = (hostLevelKey, hostId, node) => {
        const key = 'topic:' + node.id;
        if (deletingKey) return;
        setDeletingKey(key);
        const checks = topicLevel.childChecks.map((childCheck) => api.hasChildren(childCheck, node.id));
        Promise.all(checks).then((results) => {
            if (results.some(Boolean)) {
                window.alert('Неможливо видалити «' + (node.name || '') + '»: спочатку видаліть усі вкладені елементи.');
                setDeletingKey(null);
                return;
            }
            if (!window.confirm('Видалити «' + (node.name || '') + '»?')) {
                setDeletingKey(null);
                return;
            }
            api.deleteOne(topicLevel.apiBase, node.id).then(() => {
                const cacheKey = hostLevelKey + ':' + hostId;
                setTopicCache((prev) => {
                    const current = prev[cacheKey] || [];
                    return { ...prev, [cacheKey]: current.filter((item) => item.id !== node.id) };
                });
                setSelected((prev) => (prev && prev.levelKey === 'topic' && prev.id === node.id ? null : prev));
            }).catch(() => {
                window.alert("Елемент не видалено, скоріш за все він повʼязаний з іншими елементами.");
            }).finally(() => setDeletingKey(null));
        }).catch(() => setDeletingKey(null));
    };

    // Blocks the delete if the node still has children in any of its
    // level's childChecks collections. Structural child + directly-attached
    // topics are both checked now (courseLevels.js gives every hostable
    // level 2 childChecks entries) - reused from cache when already loaded
    // to avoid a redundant GET, for either collection. Topic itself checks
    // its own 2 collections (elements + exercise links), always fresh since
    // neither lives in either tree cache. Still `.catch()`s the DELETE call
    // itself as a TOCTOU backstop even after the pre-check passes.
    const deleteNode = (levelKey, parentId, node) => {
        const key = levelKey + ':' + node.id;
        if (deletingKey) return;
        const level = LEVELS_BY_KEY[levelKey];
        setDeletingKey(key);

        const checks = level.childChecks.map((childCheck) => {
            if (level.childKey && childCheck.apiBase === LEVELS_BY_KEY[level.childKey].apiBase) {
                const cached = getChildren(level.childKey, node.id);
                if (cached !== undefined) return Promise.resolve(cached.length > 0);
            }
            if (level.canHostTopics && childCheck.apiBase === topicLevel.apiBase) {
                const cachedTopics = getTopics(levelKey, node.id);
                if (cachedTopics !== undefined) return Promise.resolve(cachedTopics.length > 0);
            }
            return api.hasChildren(childCheck, node.id);
        });

        Promise.all(checks).then((results) => {
            if (results.some(Boolean)) {
                window.alert('Неможливо видалити «' + (node.name || '') + '»: спочатку видаліть усі вкладені елементи.');
                setDeletingKey(null);
                return;
            }
            if (!window.confirm('Видалити «' + (node.name || '') + '»?')) {
                setDeletingKey(null);
                return;
            }
            api.deleteOne(level.apiBase, node.id).then(() => {
                setCache((prev) => {
                    const current = (prev[levelKey] && prev[levelKey][parentId]) || [];
                    return { ...prev, [levelKey]: { ...(prev[levelKey] || {}), [parentId]: current.filter((item) => item.id !== node.id) } };
                });
                setSelected((prev) => (prev && prev.levelKey === levelKey && prev.id === node.id ? null : prev));
                setExpandedKeys((prev) => prev.filter((k) => k !== key));
            }).catch(() => {
                window.alert("Елемент не видалено, скоріш за все він повʼязаний з іншими елементами.");
            }).finally(() => setDeletingKey(null));
        }).catch(() => setDeletingKey(null));
    };

    const actions = {
        getChildren, ensureChildren, getNode,
        getTopics, ensureTopics, getTopicNode,
        isExpanded, toggleExpand, expandAll, collapseAll,
        isSelected, select,
        createNode, updateNode, deleteNode, reorderSibling,
        createTopic, updateTopic, deleteTopic, reorderTopicSibling,
        deletingKey,
    };

    return { course, loading, saveCourse, selected, actions };
}
